import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { reuseRows } from '../../../components/rows';
import { SharedHistoryApi } from './shared-history.api';
import { DAY_FIELDS, DEFAULT_HISTORY_SORT, READING_SORT_FIELDS, READING_FIELDS, buildHistoryColumns, historyTableModel, buildReadingRow } from '../../generic/patient-detail/history-table';
import { PatientAnalyticsPresenter } from '../../../components/patient-analytics/patient-analytics.presenter';
import { BucketReadingsPresenter } from '../../../components/bucket-readings/bucket-readings.presenter';
import { SharedHistoryKeys } from './shared-history.key';
import { SharedHistoryScope } from './shared-history.scope';
const LOG = Logger.get('Mormaii.SharedHistoryPresenter');
// @Inject
const sharedApi = SharedHistoryApi.INSTANCE;
/** Cinquenta por pagina, como o `rowsPerPage={50}` de la. */
const PER_PAGE = 50;
/**
 * O historico que um paciente compartilhou por link.
 *
 * Tela PUBLICA: fica fora do `hub`, entao a guarda de sessao nao se aplica - nao
 * por excecao escrita em lugar nenhum, mas porque ela nao esta atras da porta.
 *
 * Duas visoes do mesmo dado: o painel do paciente (o que abre) e a lista de
 * medicoes. As duas moram no mesmo place, distinguidas por parametro.
 */
export class SharedHistoryPresenter extends CubePresenter {
    constructor(app) {
        super(app, new SharedHistoryScope());
        this.parentSlot = NOOP_VOID;
        /** Se ja se tentou vincular o paciente, para a repeticao nao insistir. */
        this.linkAttempted = false;
        this.view = 'chart';
        /** As linhas da pagina que esta na tela, como o servidor as mandou. */
        this.entries = [];
        /** Quantas linhas o historico tem, para a conta de paginas. */
        this.total = 0;
        /** Se o cabecalho ja foi preenchido - ele nao muda de pagina para pagina. */
        this.headerLoaded = false;
        this.page = 1;
        /**
         * O modelo da tabela - o MESMO da ficha (`history`): a ordem e a largura que a
         * pessoa escolheu la valem aqui, pela mesma conta. O visitante sem login tambem
         * arrasta; so nao ha onde lembrar. A ordenacao das linhas nao passa por ele: aqui
         * quem ordena e o servidor.
         */
        this.table = historyTableModel('history', () => this.app.currentSession?.id, {
            columns: 'mormaii.historyColumns',
            widths: 'mormaii.columnWidths:history'
        });
        this.analytics = { period: '7d', from: '', to: '', metric: 'FREQUENCIA_CARDIACA' };
        /**
         * Se o periodo ainda e o PADRAO, e pode ser ajustado ao fim do link.
         *
         * Um link que cobre 19/09 a 19/10 aberto em 31/10 pedia os ultimos 7 dias de hoje,
         * o servidor recortava para 19/10 a 19/10 e tudo aparecia "Sem medição no periodo".
         * O padrao passa a ser os ultimos 7 dias DO LINK quando ele termina antes de hoje.
         * Vale so enquanto ninguem escolheu um periodo - nem o endereco, nem o leitor.
         */
        this.fitPending = false;
        /**
         * O painel do paciente, o MESMO que o detalhe do colaborador usa.
         *
         * A unica diferenca e a origem: la um paciente identificado, aqui um UUID de
         * compartilhamento. Nem o painel nem a view sabem qual das duas e.
         */
        this.analyticsPresenter = new PatientAnalyticsPresenter(this.update, async (change) => {
            // Escolha do leitor: o padrao deixa de ser da tela.
            this.fitPending = false;
            this.analytics = { ...this.analytics, ...change };
            await this.analyticsPresenter.moveTo({ kind: 'history', historyId: this.code }, this.analytics);
            this.updateHistory();
        });
        /** O detalhamento de uma celula da lista - o mesmo dialogo do detalhe do paciente. */
        this.bucketPresenter = new BucketReadingsPresenter(this.update);
        this.pending = 0;
        /**
         * Os codigos que o servidor ja respondeu: `true` vivo, `false` vencido (410).
         *
         * O link vencido disparava NOVE requisicoes, todas 410: a primeira pagina da lista e,
         * em paralelo, as oito do painel (o resumo, as seis series e o normal de temperatura).
         * A primeira ja basta para a tela dizer "Este link expirou". Entao, enquanto um codigo
         * nao respondeu, o painel e o vinculo esperam a primeira pagina; vencido, nao saem. Um
         * codigo que ja respondeu vivo nao espera mais nada.
         */
        this.answered = new Map();
    }
    async applyParameters(intent, initialization) {
        const keys = new SharedHistoryKeys(this.app, intent);
        if (initialization) {
            this.scope.onPreviousPage = this.action(this.onMovePage.bind(this, -1));
            this.scope.onNextPage = this.action(this.onMovePage.bind(this, 1));
            this.scope.onGoToPage = this.action(this.onGoToPage.bind(this));
            this.scope.analytics = this.analyticsPresenter.scope;
            this.scope.bucket = this.bucketPresenter.scope;
            this.scope.onShowList = this.action(this.onShowView.bind(this, 'list'));
            this.scope.onShowChart = this.action(this.onShowView.bind(this, 'chart'));
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        /*
         * Sem codigo nao ha o que buscar, mas HA o que dizer.
         *
         * Antes isto devolvia `false`, e a caminhada parava sem ninguem preencher o
         * slot - a pessoa via uma tela em branco. Preencher com a tela no estado de
         * aviso e o que transforma um beco sem saida numa resposta.
         */
        if (!keys.code) {
            LOG.warn('Histórico compartilhado sem código');
            this.code = undefined;
            this.showInvalid(false);
            this.scope.loading = false;
            this.parentSlot(this.scope);
            return true;
        }
        if (keys.code !== this.code) {
            this.scope.invalidLink = false;
            // O dialogo aberto falava de OUTRO historico.
            await this.bucketPresenter.close();
        }
        // Recarrega quando MUDA o que o servidor responde: outro historico, outra
        // pagina ou outra ordem. So a troca de visao reaproveita o que esta em maos.
        const reloading = keys.code !== this.code || keys.sort !== this.sort || keys.page !== this.page;
        this.code = keys.code;
        this.sort = keys.sort;
        this.page = keys.page;
        this.view = keys.view;
        this.scope.view = keys.view;
        this.analytics = {
            period: keys.period,
            from: keys.analyticsFrom,
            to: keys.analyticsTo,
            metric: keys.metric
        };
        this.fitPending = false;
        if (!keys.periodChosen) {
            if (this.fitted?.code === keys.code) {
                this.analytics = { ...this.analytics, period: 'custom', from: this.fitted.from, to: this.fitted.to };
            }
            else {
                this.fitPending = true;
            }
        }
        this.parentSlot(this.scope);
        this.scope.historyLayout = this.table.show(this);
        /*
         * As cargas correm em PARALELO, e a viagem termina aqui.
         *
         * Nenhum nivel abaixo desta tela precisa do que ela carrega, entao nao ha o que
         * esperar: o slot ja esta na tela, e o estado que identifica a face (codigo,
         * ordem, pagina, visao) ja foi lido acima. Esperar a carga dentro do
         * `applyParameters` - que neste historico chega a quarenta megabytes - deixava
         * o flip aberto por segundos, e uma acao do leitor nesse meio tempo publicava o
         * endereco do lugar ANTERIOR (a aplicacao ainda nao tinha comitado a viagem): a
         * tela voltava sozinha para o inicio. Isso era contido por uma flag `ready`, que
         * saiu em 08/10/2026 junto com a espera. Cada carga tem o proprio token de
         * requisicao, entao a resposta atrasada de uma navegacao anterior fica calada.
         *
         * A lista sempre carrega: e dela que saem o nome do paciente e o prazo do link,
         * que o cabecalho mostra nas duas visoes.
         */
        let listing;
        if (reloading || this.entries.length === 0) {
            listing = this.load();
            this.inBackground('Carregando o histórico compartilhado', listing);
        }
        else {
            this.showHistory();
        }
        const code = keys.code;
        if (this.view === 'chart') {
            this.inBackground('Carregando o gráfico do histórico', this.whenAlive(code, listing, () => this.showChart(code)));
        }
        if (keys.linkPatient) {
            this.inBackground('Vinculando o paciente', this.whenAlive(code, listing, () => this.linkToProfessional(code)));
        }
        return true;
    }
    publishParameters(intent) {
        const keys = new SharedHistoryKeys(this.app, intent);
        keys.code = this.code;
        keys.sort = this.sort;
        keys.page = this.page;
        keys.view = this.view;
        // O atalho primeiro: os setters das pontas o consultam para decidir se
        // precisam aparecer no endereco.
        keys.period = this.analytics.period;
        keys.analyticsFrom = this.analytics.from;
        keys.analyticsTo = this.analytics.to;
        keys.metric = this.analytics.metric;
        // `linkPatient` NAO e republicado: o vinculo ja aconteceu, e manter o
        // parametro faria um F5 tentar de novo. O endereco passa a descrever o que
        // esta na tela, que e o historico.
    }
    /**
     * O aviso no lugar da tela: link sem codigo, ou vencido.
     *
     * O vencido tem titulo proprio. Antes ele caia no dialogo de erro inesperado, com o
     * motivo real so na tela de tras - e o dialogo nao fechava ao sair da pagina.
     */
    showInvalid(expired) {
        this.scope.invalidTitle = expired ? 'Este link expirou' : 'Link inválido ou expirado';
        this.scope.invalidText = expired
            ? 'O prazo deste histórico compartilhado terminou. Peça um novo link a quem o compartilhou com você.'
            : 'Peça um novo link a quem compartilhou o histórico com você. Os links têm prazo de validade.';
        this.scope.invalidLink = true;
    }
    /**
     * Roda `work` so depois de o link ter dito que esta vivo - ver `answered`.
     *
     * Espera a primeira pagina apenas enquanto o codigo nao respondeu. Erro que nao seja
     * o 410 nao segura nada: a lista o reporta como sempre, e o painel tenta por conta.
     */
    async whenAlive(code, listing, work) {
        if (listing && !this.answered.has(code)) {
            await listing.catch(() => undefined);
        }
        if (this.code !== code || this.answered.get(code) === false) {
            return;
        }
        await work();
    }
    /** O painel do paciente, e o ajuste do periodo padrao ao fim do link. */
    async showChart(code) {
        if (this.answered.get(code) === false) {
            return;
        }
        await this.analyticsPresenter.moveTo({ kind: 'history', historyId: code }, this.analytics);
        await this.fitPeriod(code);
    }
    /**
     * Os ultimos 7 dias DO LINK, quando ele termina antes de hoje.
     *
     * A resposta nao traz o periodo do link, mas o servidor recorta o pedido a ele - e
     * diz o intervalo que respondeu (`periodInfo`). Um fim respondido antes do pedido e
     * o fim do link. Funciona igual com o Spring e com o Kotlin, sem rota nova: e a
     * mesma resposta que o painel ja pedia.
     *
     * Custa uma segunda carga do painel, e so para link que terminou antes de hoje.
     */
    async fitPeriod(code) {
        if (!this.fitPending || this.code !== code) {
            return;
        }
        const asked = this.analytics;
        const answered = this.analyticsPresenter.answeredPeriod;
        // Sem resposta (falha de rede, link vencido) nao ha o que ajustar - ainda.
        if (!answered) {
            return;
        }
        this.fitPending = false;
        if (asked.period !== '7d' || !answered.to || answered.to >= asked.to) {
            return;
        }
        const from = daysBefore(answered.to, 6);
        this.fitted = { code, from, to: answered.to };
        this.analytics = { ...asked, period: 'custom', from, to: answered.to };
        await this.analyticsPresenter.moveTo({ kind: 'history', historyId: code }, this.analytics);
        this.updateHistory();
    }
    /** Uma carga que corre fora do flip: o erro que escapar chega ao leitor, e nao some. */
    inBackground(what, work) {
        work.catch((caught) => this.app.unexpected(what, caught));
    }
    /**
     * Uma PAGINA do historico, ordenada pelo servidor.
     *
     * Pedir o historico inteiro para cortar no cliente custava 40 MB por tela num
     * dos historicos de QA. Agora a pagina e a ordenacao sao do servidor
     * (`sortMeasurementTypeId`/`sortDirection`), e cada tela pesa 15 KB.
     */
    async load() {
        if (!this.code) {
            return;
        }
        const token = ++this.pending;
        this.scope.loading = true;
        const code = this.code;
        try {
            const page = await sharedApi.fetchSharedHistory(code, this.page, PER_PAGE, this.sort);
            this.answered.set(code, true);
            if (token !== this.pending) {
                return;
            }
            this.entries = page.elements ?? [];
            this.total = page.count ?? this.entries.length;
            await this.loadHeader();
        }
        catch (caught) {
            const expired = caught?.status === 410;
            if (expired) {
                this.answered.set(code, false);
            }
            if (token !== this.pending) {
                return;
            }
            this.entries = [];
            this.total = 0;
            // O 410 e o link VENCIDO: um estado da tela, e nao um erro inesperado.
            if (expired) {
                LOG.info('Shared history expired');
                this.fitPending = false;
                this.showInvalid(true);
                return;
            }
            this.app.unexpected('Abrindo o histórico compartilhado', caught);
        }
        finally {
            if (token === this.pending) {
                this.scope.loading = false;
                this.showHistory();
            }
        }
    }
    /**
     * O nome do paciente, a ultima atualizacao e o prazo do link.
     *
     * Os tres vem da leitura mais RECENTE, e nao da primeira linha da pagina: com
     * a ordenacao no servidor, a primeira linha de uma pagina ordenada por passos
     * nao tem nada a ver com "ultima atualizacao". A referencia le
     * `elements[0]` de uma primeira pagina sem ordenacao, que e a mesma coisa.
     *
     * Uma requisicao so por tela, e de UMA linha - e por isso que ela pode ser
     * separada sem custo.
     */
    async loadHeader() {
        if (this.headerLoaded || !this.code) {
            return;
        }
        // O cabecalho quer a hora MAIS RECENTE. Ela e a primeira linha da pagina 1 so na
        // ordem padrao; ordenada por uma coluna (de 08/10/2026 em diante, de novo), a
        // primeira linha e outra, e a mais recente e pedida a parte.
        const naOrdemNatural = this.page === 1 && (this.sort === undefined || this.sort === DEFAULT_HISTORY_SORT);
        const first = naOrdemNatural
            ? this.entries[0]
            : (await sharedApi.fetchSharedHistory(this.code, 1, 1)).elements?.[0];
        this.headerLoaded = true;
        this.scope.patientName = first?.name || '--';
        this.scope.lastUpdate = first ? formatInstant(first.date) : '--';
        this.scope.expiresAt = first?.expirationDate ? formatInstant(first.expirationDate) : '--';
    }
    /**
     * A tabela na tela.
     *
     * As linhas chegam na ordem e na pagina que o servidor mandou - aqui nao se
     * ordena nem se corta nada.
     */
    showHistory() {
        // A primeira coluna - a hora - nao sai do lugar; as outras, na ordem escolhida.
        const [first, ...movable] = READING_FIELDS;
        const order = this.table.preferences.arrange(movable.map((column) => column.field));
        const fields = [first, ...order.map((field) => movable.find((column) => column.field === field))];
        this.table.setVisible(fields.map((column) => column.field));
        this.scope.historyColumns = buildHistoryColumns(fields, this.sort, this.update, (field) => this.onSort(field), (source, target) => this.onMoveColumn(source, target), this.scope.historyColumns);
        this.scope.historyPageCount = Math.max(1, Math.ceil(this.total / PER_PAGE));
        this.scope.historyPage = Math.min(Math.max(1, this.page), this.scope.historyPageCount);
        // As linhas que ja estao na tela sao atualizadas, e nao recriadas - ver `reuseRows`.
        this.scope.historyRows = reuseRows(this.scope.historyRows, this.entries, (entry) => entry.date, (entry, reuse) => buildReadingRow(entry, this.update, (typeId, hour) => this.openBucket(typeId, hour), fields, reuse));
    }
    /**
     * Abre o detalhamento de uma celula: as leituras daquela hora, pelo codigo do link.
     *
     * O dialogo e o do detalhe do paciente; daqui sai so a busca e o que a falha quer
     * dizer. O 410 e o link que venceu com a tela aberta: a tela passa ao aviso de link
     * vencido, como na primeira pagina, e o codigo fica marcado - nenhuma outra
     * requisicao sai por ele. Com o codigo ja sabido vencido, nem esta sai.
     */
    async openBucket(typeId, hour) {
        const code = this.code;
        if (!code || this.answered.get(code) === false) {
            return;
        }
        try {
            await this.bucketPresenter.open(typeId, hour, () => sharedApi.fetchBucketReadings(code, typeId, hour));
        }
        catch (caught) {
            if (caught?.status === 410) {
                LOG.info('Shared history expired while open');
                this.answered.set(code, false);
                if (this.code === code) {
                    this.fitPending = false;
                    this.showInvalid(true);
                }
                return;
            }
            LOG.warn('Could not load the bucket readings', caught);
            this.app.alert('error', 'Detalhamento', 'Não foi possível carregar as medições desta hora.');
        }
    }
    /**
     * Vincula o paciente ao profissional que abriu o link.
     *
     * So acontece para quem esta logado como profissional: para um visitante nao ha
     * a quem vincular, e o backend recusaria. A tentativa e uma so por sessao da
     * tela - o painel antigo precisa de um `useRef` para o mesmo efeito, porque o
     * `useEffect` dele dispara de novo a cada mudanca de `user`.
     */
    async linkToProfessional(code) {
        if (this.linkAttempted) {
            return;
        }
        const roles = this.app.currentSession?.roles ?? [];
        if (!roles.includes('ROLE_PROFESSIONAL')) {
            return;
        }
        this.linkAttempted = true;
        try {
            await sharedApi.linkSharedHistoryToProfessional(code);
            // Titulo e rotulo do botao sao os do painel antigo.
            this.app.notify('Paciente adicionado', 'O paciente foi adicionado aos seus monitoramentos.', 'Okay');
        }
        catch (caught) {
            LOG.warn('Could not link the patient to the professional', caught);
        }
    }
    /**
     * Troca de visao.
     *
     * Nao e uma viagem: e o mesmo place com outro parametro. O painel so busca
     * quando aparece pela primeira vez - abrir a lista e voltar nao repete rede.
     */
    async onShowView(view) {
        if (this.view === view) {
            return;
        }
        this.view = view;
        this.scope.view = view;
        if (view === 'chart' && this.code) {
            await this.showChart(this.code);
        }
        this.updateHistory();
    }
    /**
     * Um clique na coluna ordena por ela; o segundo inverte.
     *
     * Ordenar e VOLTAR AO SERVIDOR, porque quem ordena e ele - ordenar so as
     * cinquenta linhas da pagina seria outra coisa, e nao o que a tela promete.
     */
    async onSort(field) {
        // So o que o servidor sabe ordenar: a mesma lista branca da URL.
        if (!READING_SORT_FIELDS.includes(field)) {
            return;
        }
        const current = this.sort ?? DEFAULT_HISTORY_SORT;
        this.sort = current === field ? `-${field}` : field;
        this.page = 1;
        await this.load();
        this.updateHistory();
    }
    /** Como no detalhe: preferencia de quem le, fora do endereco. */
    async onMoveColumn(source, target) {
        const movable = DAY_FIELDS.slice(1).map((column) => column.field);
        if (this.table.preferences.move(movable, source, target)) {
            this.showHistory();
        }
    }
    async onMovePage(direction) {
        await this.onGoToPage(this.scope.historyPage + direction);
    }
    async onGoToPage(page) {
        const wanted = Math.min(Math.max(1, page), this.scope.historyPageCount);
        if (wanted === this.page) {
            return;
        }
        this.page = wanted;
        await this.load();
        this.updateHistory();
    }
}
/**
 * `dd/MM/yyyy HH:mm:ss`, que e o formato do cabecalho e dos dois campos.
 *
 * Por fatia de texto, e nao por `Date`: a API manda o instante sem fuso, e passar
 * por `Date` e o que fez o painel antigo mostrar o dia anterior em duas telas.
 */
function formatInstant(iso) {
    const [year, month, day] = iso.slice(0, 10).split('-');
    const time = iso.length >= 19 ? iso.slice(11, 19) : '00:00:00';
    return `${day}/${month}/${year} ${time}`;
}
/** O dia `count` dias antes de `day` (`YYYY-MM-DD`), ao meio-dia para o fuso nao empurrar. */
function daysBefore(day, count) {
    const [year, month, date] = day.split('-').map(Number);
    const moved = new Date(year, month - 1, date - count, 12);
    const pad = (value) => String(value).padStart(2, '0');
    return `${moved.getFullYear()}-${pad(moved.getMonth() + 1)}-${pad(moved.getDate())}`;
}
//# sourceMappingURL=shared-history.presenter.js.map